import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from '../../../../../app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from '../../../app/shared/reducers/reducer.utils';
import { defaultValue, ICaseLawyer } from '../../shared/model/case-lawyer.model';
import { ICaseReview } from '../../shared/model/case-forms.model';

const initialState: EntityState<ICaseLawyer> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  steps: [],
};

const apiUrl = 'api/workflows/steps';
const formSaveApi = 'api/workflows/step';
// const getChatId = `api/workflows/${process.env.IS_DEV ? '4775' : '1511'}/start`;
const getChatId = `api/workflows/1511/start`;

// Actions

export const getEntities = createAsyncThunk(
  'caseReview/fetch_entity_list',
  async ({ page, size, sort, query }: IQueryParams) => {
    const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&` : ''}cacheBuster=${new Date().getTime()}`;
    return axios.get<ICaseReview[]>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'caseReview/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ICaseReview>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getLawyerAiChatId = createAsyncThunk(
  'caseReview/lawyer_aichat_id',
  async (id: string | number) => {
    const requestUrl = `${getChatId}?extRefKey=${id}`;
    // const requestUrl = `${getChatId}`;

    const response = await axios.get<ICaseLawyer>(requestUrl);
    localStorage.setItem('lawyerAiChatId', JSON.stringify(response.data));
    return response;
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'caseReview/create_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.post<ICaseReview>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getSubscription = async (id: any) => {
  const requestUrl = `api/stripe/dyhac/user-subscriptions/${id}`;
  try {
    const response = await axios.get(requestUrl, {
      headers: {
        'Content-Type': 'application/json',
        'Workflow-Authorization': 'Dyhac$25!',
      },
    });
    if (response.status == 200 && Array.isArray(response.data) && response.data.length > 0) {
      response?.data[0] ?? checkPlanStatus(response?.data[0]?.startDate, response?.data[0]?.endDate);
      const latestPlan = Array.isArray(response?.data) ? response?.data[response?.data.length - 1] : response?.data;
      return latestPlan;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const checkPlanStatus = (startDate, endDate) => {
  const now = Date.now();

  if (now < startDate) {
    return 'Not started';
  } else if (now >= startDate && now <= endDate) {
    return 'Active';
  } else {
    return 'Expired';
  }
};

export const createLawyerForms = createAsyncThunk(
  'caseReview/create_lawyer_forms',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.get(requestUrl);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const saveLawyerForms = createAsyncThunk(
  'caseReview/save_forms',
  async (data: any, thunkAPI) => {
    const requestUrl = `${formSaveApi}/${data?.aiChatId}?stepNum=0`;
    const result = await axios.post(requestUrl, data?.formList);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const setFormStep = createAsyncThunk(
  'caseReview/set_steps',
  async (step: any, thunkAPI) => {
    // thunkAPI.dispatch(getEntities({}));
    return step;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'caseReview/update_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.put<ICaseReview>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'caseReview/partial_update_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.patch<ICaseReview>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'caseReview/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ICaseReview>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CaseLawyerSlice = createEntitySlice({
  name: 'caseLawyer',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(getLawyerAiChatId.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(createLawyerForms.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.loading = false;
        state.entities = action.payload.data?.data?.parentWorkflowStepMetaDataFields;
      })
      .addCase(createLawyerForms.pending, state => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = true;
        // state.entity = {};
      })
      .addCase(createLawyerForms.rejected, state => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = false;
        state.entities = [];
      })
      .addCase(saveLawyerForms.pending, state => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addCase(saveLawyerForms.rejected, state => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = false;
        state.entity = {};
      })
      .addCase(setFormStep.pending, state => {
        state.loading = true;
      })
      .addCase(setFormStep.fulfilled, (state, action) => {
        state.loading = false;
        if (state.entity) {
          // state.entity.step = action.payload;
        }
      })
      .addMatcher(isFulfilled(saveLawyerForms), (state, action) => {
        const { data, headers } = action.payload;
        return {
          ...state,
          loading: false,
          updateSuccess: true,
          entities: action.payload.data?.parentWorkflowStepMetaDataFields,
          step: '3',
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = CaseLawyerSlice.actions;

// Reducer
export default CaseLawyerSlice.reducer;
